import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  IconButton,
  Chip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Pagination,
  TextField,
  Alert,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GetAppIcon from '@mui/icons-material/GetApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BadgeIcon from '@mui/icons-material/Badge';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';
import { ListPageLayout, EmptyState } from '../../components/ListPage';
import { buildSingleIdCardHtml } from '../../utils/idCardTemplate';
import { fetchIdCardPublicAssetsAsDataUrls } from '../../utils/idCardInlineAssets';
import { resolveMemberCardPhotoDataUrl } from '../../utils/memberUploadPhotoDataUrl';
import { renderIdCardHtmlToPdfBlobWithTimeout } from '../../utils/idCardPdfExport';
import JSZip from 'jszip';

const statusOptions = ['All', 'Active', 'Pending', 'Suspended', 'Withdrawn', 'Alumni', 'Deceased', 'Minor', 'Pensioners', 'Students'];
const statusChangeOptions = ['Active', 'Pending', 'Suspended', 'Withdrawn', 'Alumni', 'Deceased', 'Minor', 'Pensioners', 'Students'];
const CARD_PHOTO_PAGE_SIZE = 15;

async function messageFromAxiosError(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch {
      /* ignore non-JSON blob */
    }
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (err?.response?.status === 502) {
    return 'The server timed out loading this export. Try a smaller selection, or retry.';
  }
  if (err?.response?.status === 304) {
    return 'The download was served from cache and was empty. Please try again.';
  }
  return err?.message || fallback;
}

async function downloadBlobFile(blob, filename) {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error('Export returned an empty file. Please try again.');
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function MembersList() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Active');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, action: null });
  const [pendingMembers, setPendingMembers] = useState([]);
  const [deleteDialogMember, setDeleteDialogMember] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  const [downloadingCards, setDownloadingCards] = useState(false);
  const [downloadingCardsPdf, setDownloadingCardsPdf] = useState(false);
  const [downloadingNewCards, setDownloadingNewCards] = useState(false);
  const [downloadingNewCardsPdf, setDownloadingNewCardsPdf] = useState(false);
  const [newCardsCount, setNewCardsCount] = useState(null);
  const [pdfExportProgress, setPdfExportProgress] = useState(null);
  /** After async PDF ZIP build: user must click Download (avoids browser blocking long async saves). */
  const [idCardsPdfReady, setIdCardsPdfReady] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let qrLibPromise = null;

  useEffect(() => {
    const q = searchParams.get('search');
    if (q != null && q !== '') setSearch(q);
    const st = searchParams.get('status');
    if (st) setStatus(st);
  }, [searchParams]);

  const fetchPendingMembers = async () => {
    try {
      const response = await api.get('/members/pending', {
        params: { limit: 50 },
      });
      setPendingMembers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching pending members:', err);
      setPendingMembers([]);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/members', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
          status: status === 'All' ? 'all' : status,
        },
      });
      setMembers(response.data.data || []);
      setTotal(Number(response.data.pagination?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch members');
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [page, rowsPerPage, status]);

  useEffect(() => {
    fetchPendingMembers();
  }, []);

  const fetchNewCardsCount = async () => {
    try {
      const response = await api.get('/members/cards/not-downloaded-count', {
        params: { status: status === 'All' ? 'all' : status },
      });
      setNewCardsCount(Number(response.data?.count) || 0);
    } catch (err) {
      console.error('Error fetching new card download count:', err);
      setNewCardsCount(null);
    }
  };

  useEffect(() => {
    fetchNewCardsCount();
  }, [status]);

  const markExportedCardsDownloaded = async (memberIds) => {
    if (!memberIds?.length) return;
    try {
      await api.post('/members/cards/mark-downloaded', { member_ids: memberIds });
      await fetchNewCardsCount();
    } catch (err) {
      console.error('Failed to mark ID cards as downloaded:', err);
    }
  };

  const fetchCardMembersForExport = async ({ selectedOnly, newOnly, onProgress }) => {
    const selectedIds = selectedOnly ? selectedMembers : [];
    if (selectedOnly && selectedIds.length === 0) {
      throw new Error('Select at least one member to download ID cards');
    }
    const baseParams = {
      status: status === 'All' ? 'all' : status,
      search: newOnly ? undefined : search || undefined,
      member_ids: selectedIds.length ? selectedIds.join(',') : undefined,
      not_downloaded: newOnly ? true : undefined,
      include_profile_blobs: true,
    };
    const all = [];
    let page = 1;
    let lastPage = 1;
    let pageSize = CARD_PHOTO_PAGE_SIZE;
    while (page <= lastPage) {
      let response;
      try {
        response = await api.get('/members/cards/data', {
          params: { ...baseParams, page, limit: pageSize, _: Date.now() },
          timeout: 90000,
        });
      } catch (err) {
        const retryable = err.response?.status === 502 || err.code === 'ECONNABORTED';
        if (retryable && page === 1 && all.length === 0 && pageSize > 5) {
          pageSize = Math.max(5, Math.floor(pageSize / 2));
          continue;
        }
        throw err;
      }
      const batch = Array.isArray(response.data?.data) ? response.data.data : [];
      const pagination = response.data?.pagination;
      all.push(...batch);
      if (!pagination) break;
      const total = Number(pagination.total) || all.length;
      lastPage = Math.max(1, Number(pagination.last_page) || 1);
      if (typeof onProgress === 'function') {
        onProgress({ current: all.length, total });
      }
      if (!batch.length || pagination.hasMore === false || page >= lastPage) break;
      page += 1;
      if (page > 400) break;
    }
    return all;
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchMembers();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDeleteDialog = (member) => {
    setError('');
    setDeleteDialogMember(member);
    setDeleteConfirmText('');
  };

  const closeDeleteDialog = () => {
    if (deleteSubmitting) return;
    setDeleteDialogMember(null);
    setDeleteConfirmText('');
  };

  const submitPermanentDelete = async () => {
    if (!deleteDialogMember) return;
    const expected = String(deleteDialogMember.member_id || '').trim();
    if (deleteConfirmText.trim() !== expected) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/members/${deleteDialogMember.id}`, {
        data: { confirmation: deleteConfirmText.trim() },
      });
      setSuccess('Member permanently removed from the database');
      closeDeleteDialog();
      fetchMembers();
      fetchPendingMembers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete member');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleStatusChange = async (memberId, newStatus) => {
    try {
      setUpdatingStatus(memberId);
      setError('');
      setSuccess('');
      const response = await api.patch(`/members/${memberId}/status`, { status: newStatus });
      setSuccess(response.data.message || 'Status updated successfully');
      fetchMembers();
      fetchPendingMembers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleApprove = async (memberId) => {
    await handleStatusChange(memberId, 'Active');
  };

  const handleDecline = async (memberId) => {
    if (!window.confirm('Are you sure you want to decline this member? They will be marked as Suspended.')) {
      return;
    }
    await handleStatusChange(memberId, 'Suspended');
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedMembers(members.map(m => m.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const openDeleteForSelectedSingle = () => {
    if (selectedMembers.length !== 1) {
      setError('Select exactly one member to permanently delete');
      return;
    }
    const selectedId = selectedMembers[0];
    const member = [...members, ...pendingMembers].find((m) => m.id === selectedId);
    if (!member) {
      setError('Selected member is not available in the current list');
      return;
    }
    openDeleteDialog(member);
  };

  const openBulkMenu = (event) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const closeBulkMenu = () => {
    setBulkMenuAnchor(null);
  };

  const handleBulkAction = async (actionStatus) => {
    if (selectedMembers.length === 0) {
      setError('Please select at least one member');
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await api.post('/members/bulk-status', {
        member_ids: selectedMembers,
        status: actionStatus
      });
      setSuccess(response.data.message || 'Members updated successfully');
      setSelectedMembers([]);
      setBulkActionDialog({ open: false, action: null });
      fetchMembers();
      fetchPendingMembers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update members');
    }
  };

  const handleBulkApprove = () => {
    setBulkActionDialog({ open: true, action: 'Active' });
  };

  const handleBulkDecline = () => {
    setBulkActionDialog({ open: true, action: 'Suspended' });
  };

  const handleExport = async (format) => {
    try {
      setError('');
      const requestExport = () =>
        api.get('/members/export', {
          params: {
            format,
            status: status === 'All' ? 'all' : status,
            search: search || undefined,
            _: Date.now(),
          },
          responseType: 'blob',
          timeout: 120000,
          validateStatus: (s) => s === 200 || s === 304,
        });
      let response = await requestExport();
      if (response.status === 304 || !response.data?.size) {
        response = await requestExport();
      }
      const contentType = String(response.headers?.['content-type'] || '');
      if (response.status !== 200 || contentType.includes('application/json')) {
        const text = response.data instanceof Blob ? await response.data.text() : '';
        let message = 'Failed to export members';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      await downloadBlobFile(blob, `members.${ext}`);
    } catch (err) {
      setError(await messageFromAxiosError(err, 'Failed to export members'));
    }
  };

  const buildVerifyUrl = (member) => `${window.location.origin}/verify/${member.id}`;

  const fetchQrDataUrl = async (member) => {
    try {
      if (!qrLibPromise) qrLibPromise = import('qrcode');
      const qr = await qrLibPromise;
      const lib = qr?.default || qr;
      return await lib.toDataURL(buildVerifyUrl(member), {
        margin: 0,
        width: 88,
        errorCorrectionLevel: 'M',
      });
    } catch {
      return null;
    }
  };

  const handleDownloadIdCards = async ({ selectedOnly, newOnly = false }) => {
    try {
      if (newOnly) setDownloadingNewCards(true);
      else setDownloadingCards(true);
      setError('');
      const cardMembers = await fetchCardMembersForExport({ selectedOnly, newOnly });
      if (!cardMembers.length) {
        setError(
          newOnly
            ? 'No new ID cards to download — every member matching this status has already been exported.'
            : 'No members found for ID card download'
        );
        return;
      }

      const [inlinePublicAssets, cards] = await Promise.all([
        fetchIdCardPublicAssetsAsDataUrls(),
        Promise.all(
          cardMembers.map(async (member) => ({
            member,
            photoDataUrl: await resolveMemberCardPhotoDataUrl(api, member),
            qrDataUrl: await fetchQrDataUrl(member),
          }))
        ),
      ]);

      const zip = new JSZip();
      for (const card of cards) {
        const html = buildSingleIdCardHtml(card, { inlinePublicAssets });
        const memberCode = (card.member.member_id || card.member.association_number || card.member.id || 'member')
          .toString()
          .replace(/[^a-zA-Z0-9_-]/g, '_');
        zip.file(`id-card-${memberCode}.html`, html);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      const suffix = selectedOnly ? 'selected' : newOnly ? 'new' : 'filtered';
      link.href = zipUrl;
      link.setAttribute('download', `id-cards-${suffix}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(zipUrl);
      if (!selectedOnly) {
        await markExportedCardsDownloaded(cardMembers.map((m) => m.id));
      }
      setSuccess(
        newOnly
          ? `Downloaded ${cards.length} new member ID card(s). They will not appear in the next "new only" export.`
          : `Downloaded ${cards.length} ID cards in one ZIP file.`
      );
    } catch (err) {
      setError(await messageFromAxiosError(err, 'Failed to download ID cards'));
    } finally {
      setDownloadingCards(false);
      setDownloadingNewCards(false);
    }
  };

  const memberPdfExportLabel = (member) => {
    const name = [member.first_name, member.last_name].filter(Boolean).join(' ') || 'Member';
    const id = member.member_id || member.association_number || member.id || '—';
    return `${name} (${id})`;
  };

  const pdfExportButtonLabel = (selectedOnly, newOnly = false) => {
    const busy = newOnly ? downloadingNewCardsPdf : downloadingCardsPdf;
    if (!busy) {
      if (newOnly) {
        const n = newCardsCount != null ? newCardsCount : '…';
        return `Download New Cards PDF (${n})`;
      }
      return selectedOnly ? 'Download Selected Cards PDF' : 'Download ID Cards PDF';
    }
    if (pdfExportProgress?.total) {
      return `Preparing PDF ${pdfExportProgress.current}/${pdfExportProgress.total}...`;
    }
    return 'Preparing PDF...';
  };

  const newCardsButtonLabel = () => {
    if (downloadingNewCards) return 'Preparing new cards...';
    const n = newCardsCount != null ? newCardsCount : '…';
    return `Download New ID Cards (${n})`;
  };

  const handleDownloadIdCardsPdf = async ({ selectedOnly, newOnly = false }) => {
    try {
      if (newOnly) setDownloadingNewCardsPdf(true);
      else setDownloadingCardsPdf(true);
      setPdfExportProgress(null);
      setError('');
      const cardMembers = await fetchCardMembersForExport({
        selectedOnly,
        newOnly,
        onProgress: (p) => setPdfExportProgress(p),
      });
      if (!cardMembers.length) {
        setError(
          newOnly
            ? 'No new ID cards to download — every member matching this status has already been exported.'
            : 'No members found for ID card PDF export'
        );
        if (newOnly) setDownloadingNewCardsPdf(false);
        else setDownloadingCardsPdf(false);
        return;
      }

      if (!selectedOnly && !newOnly && cardMembers.length > 1) {
        const estimateMin = Math.max(1, Math.ceil((cardMembers.length * 5) / 60));
        let confirmMsg =
          `This will generate ${cardMembers.length} PDF file(s) (every member matching your current status/search filters — there is no 250 cap).\n\n` +
          `Estimated time: about ${estimateMin}+ minute(s). Keep this tab open until finished.\n`;
        if (cardMembers.length > 100) {
          confirmMsg +=
            '\nFor 100+ members, exporting in smaller batches (select members → Download Selected Cards PDF) is more reliable in the browser.';
        }
        confirmMsg += '\n\nContinue?';
        if (!window.confirm(confirmMsg)) {
          setDownloadingCardsPdf(false);
          return;
        }
      }

      if (newOnly && cardMembers.length > 1) {
        const estimateMin = Math.max(1, Math.ceil((cardMembers.length * 5) / 60));
        const confirmMsg =
          `This will generate ${cardMembers.length} PDF file(s) for members whose cards have not been exported yet.\n\n` +
          `Estimated time: about ${estimateMin}+ minute(s). Keep this tab open until finished.\n\nContinue?`;
        if (!window.confirm(confirmMsg)) {
          setDownloadingNewCardsPdf(false);
          return;
        }
      }

      const [inlinePublicAssets, cards] = await Promise.all([
        fetchIdCardPublicAssetsAsDataUrls(),
        Promise.all(
          cardMembers.map(async (member) => ({
            member,
            photoDataUrl: await resolveMemberCardPhotoDataUrl(api, member),
            qrDataUrl: await fetchQrDataUrl(member),
          }))
        ),
      ]);

      const zip = new JSZip();
      const failed = [];
      for (let index = 0; index < cards.length; index += 1) {
        const card = cards[index];
        const memberCode = (card.member.member_id || card.member.association_number || card.member.id || 'member')
          .toString()
          .replace(/[^a-zA-Z0-9_-]/g, '_');
        setPdfExportProgress({ current: index + 1, total: cards.length });
        try {
          const html = buildSingleIdCardHtml(card, { inlinePublicAssets });
          const pdfBlob = await renderIdCardHtmlToPdfBlobWithTimeout(html, memberCode);
          if (!(pdfBlob instanceof Blob) || pdfBlob.size === 0) {
            throw new Error('empty PDF');
          }
          zip.file(`id-card-${memberCode}.pdf`, pdfBlob);
        } catch (err) {
          failed.push({
            memberCode,
            label: memberPdfExportLabel(card.member),
            message: err.message || 'PDF failed',
          });
        }
      }

      const successCount = cards.length - failed.length;
      if (successCount === 0) {
        throw new Error(
          failed[0]?.message || 'Failed to generate any ID card PDFs'
        );
      }

      if (failed.length > 0) {
        const reportLines = [
          'ID Card PDF export — members not included',
          `Generated: ${new Date().toLocaleString()}`,
          `Succeeded: ${successCount} PDF(s) in this ZIP`,
          `Failed: ${failed.length}`,
          '',
          'The following members did not get a PDF (check name/ID and retry individually if needed):',
          '',
          ...failed.map(
            (f, i) =>
              `${i + 1}. ${f.label}\n   Expected file: id-card-${f.memberCode}.pdf\n   Reason: ${f.message}`
          ),
        ];
        zip.file('EXPORT-FAILURES.txt', reportLines.join('\n'));
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const suffix = selectedOnly ? 'selected' : newOnly ? 'new' : 'filtered';
      const filename = `id-cards-pdf-${suffix}.zip`;
      let successMessage = `Prepared ${successCount} of ${cards.length} print-ready PDF(s) (high-quality RGB).`;
      if (failed.length > 0) {
        successMessage += ` ${failed.length} failed — see EXPORT-FAILURES.txt in the ZIP and the list in the dialog.`;
      }
      if (!selectedOnly) {
        const exportedIds = cards
          .filter((card) => {
            const memberCode = (card.member.member_id || card.member.association_number || card.member.id || 'member')
              .toString()
              .replace(/[^a-zA-Z0-9_-]/g, '_');
            return !failed.some((f) => f.memberCode === memberCode);
          })
          .map((card) => card.member.id);
        await markExportedCardsDownloaded(exportedIds);
      }
      if (newOnly) {
        successMessage += ' These members will not appear in the next "new only" export.';
      }
      setIdCardsPdfReady({
        url: zipUrl,
        filename,
        count: successCount,
        totalRequested: cards.length,
        failedExports: failed,
        successMessage,
      });
      const link = document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess(successMessage);
    } catch (err) {
      setError(await messageFromAxiosError(err, 'Failed to download ID cards PDF'));
    } finally {
      setDownloadingCardsPdf(false);
      setDownloadingNewCardsPdf(false);
      setPdfExportProgress(null);
    }
  };

  const closeIdCardsPdfReadyDialog = () => {
    if (idCardsPdfReady?.url) {
      try {
        window.URL.revokeObjectURL(idCardsPdfReady.url);
      } catch (_) {
        /* ignore */
      }
    }
    setIdCardsPdfReady(null);
  };

  const handleIdCardsPdfDownloaded = () => {
    if (!idCardsPdfReady) return;
    setSuccess(idCardsPdfReady.successMessage);
    window.setTimeout(() => closeIdCardsPdfReadyDialog(), 1500);
  };

  const getStatusColor = (status) => {
    const colors = {
      Active: 'success',
      Pending: 'warning',
      Suspended: 'error',
      Withdrawn: 'default',
      Alumni: 'info',
      Deceased: 'default',
      Minor: 'secondary',
      Pensioners: 'info',
      Students: 'primary',
    };
    return colors[status] || 'default';
  };

  return (
    <ListPageLayout
      title="Members"
      crumbs={<>Registry / <Box component="b" sx={{ color: 'text.primary', fontWeight: 500 }}>Members</Box></>}
      searchPlaceholder="Search name, ID or phone…"
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      success={success}
      onSuccessClose={() => setSuccess('')}
      filters={
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            size="small"
            onClick={() => handleExport('excel')}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            size="small"
            onClick={() => handleExport('csv')}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<BadgeIcon />}
            size="small"
            disabled={downloadingCards}
            onClick={() => handleDownloadIdCards({ selectedOnly: false })}
          >
            {downloadingCards ? 'Preparing cards...' : 'Download ID Cards'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<BadgeIcon />}
            size="small"
            disabled={
              downloadingNewCards ||
              downloadingCards ||
              newCardsCount === 0
            }
            onClick={() => handleDownloadIdCards({ selectedOnly: false, newOnly: true })}
          >
            {newCardsButtonLabel()}
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            size="small"
            disabled={downloadingCardsPdf}
            onClick={() => handleDownloadIdCardsPdf({ selectedOnly: false })}
          >
            {pdfExportButtonLabel(false)}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<GetAppIcon />}
            size="small"
            disabled={
              downloadingNewCardsPdf ||
              downloadingCardsPdf ||
              newCardsCount === 0
            }
            onClick={() => handleDownloadIdCardsPdf({ selectedOnly: false, newOnly: true })}
          >
            {pdfExportButtonLabel(false, true)}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/members/new')}
          >
            Add Member
          </Button>
        </>
      }
    >
      {newCardsCount != null && newCardsCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>{newCardsCount}</strong> member{newCardsCount === 1 ? '' : 's'} matching the current status
          filter ha{newCardsCount === 1 ? 's' : 've'} not had ID cards exported yet. Use{' '}
          <strong>Download New ID Cards</strong> or <strong>Download New Cards PDF</strong> to export only
          those members (already-exported cards are skipped). A full download also marks members as exported.
        </Alert>
      )}

      {/* New members awaiting approval - visible when status is not Pending (so admin sees them without filtering) */}
      {pendingMembers.length > 0 && status !== 'Pending' && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'warning.light',
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonAddIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              New members awaiting approval ({pendingMembers.length})
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {pendingMembers.map((member) => {
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: 'warning.main',
                      '&:hover': { boxShadow: 2 },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 1.5, pb: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
                        {member.member_id}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/members/details/${member.id}`)}
                          title="View"
                          sx={{ color: 'primary.main' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/members/${member.id}`)}
                          title="Edit"
                          sx={{ color: 'text.secondary' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
                      <MemberProfileAvatar
                        member={member}
                        size={80}
                        sx={{ mb: 1.5, border: '2px solid' }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', mb: 0.5 }}>
                        {member.first_name} {member.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 0.5, fontSize: '0.8rem' }}>
                        {member.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1.5, fontSize: '0.8rem' }}>
                        {member.phone}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(member.id)}
                            disabled={updatingStatus === member.id}
                          >
                            {updatingStatus === member.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Decline">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDecline(member.id)}
                            disabled={updatingStatus === member.id}
                          >
                            {updatingStatus === member.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CancelIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/dashboard/members/${member.id}`)}
                        >
                          Edit
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {selectedMembers.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.selected' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {selectedMembers.length} member(s) selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleBulkApprove}
            >
              Approve Selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleBulkDecline}
            >
              Decline Selected
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Change Status</InputLabel>
              <Select
                value=""
                label="Change Status"
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkAction(e.target.value);
                  }
                }}
              >
                {statusChangeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    Set to {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              onClick={() => setSelectedMembers([])}
            >
              Clear Selection
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BadgeIcon />}
              disabled={downloadingCards}
              onClick={() => handleDownloadIdCards({ selectedOnly: true })}
            >
              {downloadingCards ? 'Preparing cards...' : 'Download Selected Cards'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<GetAppIcon />}
              disabled={downloadingCardsPdf}
              onClick={() => handleDownloadIdCardsPdf({ selectedOnly: true })}
            >
              {pdfExportButtonLabel(true)}
            </Button>
            <Tooltip title="More actions">
              <IconButton size="small" onClick={openBulkMenu}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={bulkMenuAnchor}
              open={Boolean(bulkMenuAnchor)}
              onClose={closeBulkMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  closeBulkMenu();
                  openDeleteForSelectedSingle();
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
                Danger Zone: Delete Permanently
              </MenuItem>
            </Menu>
          </Box>
        </Paper>
      )}

      {/* Select All Checkbox */}
      {members.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            indeterminate={selectedMembers.length > 0 && selectedMembers.length < members.length}
            checked={members.length > 0 && selectedMembers.length === members.length}
            onChange={handleSelectAll}
          />
          <Typography variant="body2" color="text.secondary">
            Select All
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : members.length === 0 ? (
        <EmptyState
          title="No members found"
          description="Add your first member to get started. You can register members manually or they can self-register through the member portal."
          actionLabel="Add Member"
          onAction={() => navigate('/dashboard/members/new')}
        />
      ) : (
        <>
          <Grid container spacing={3}>
            {members.map((member) => {
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: selectedMembers.includes(member.id) ? '2px solid' : '1px solid',
                      borderColor: selectedMembers.includes(member.id) ? 'primary.main' : 'divider',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    {/* Top Section: Member ID and Action Icons */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        p: 1.5,
                        pb: 0,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={selectedMembers.includes(member.id)}
                          onChange={() => handleSelectMember(member.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                          {member.member_id}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/members/details/${member.id}`)}
                          title="View"
                          sx={{ color: 'primary.main' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/dashboard/members/${member.id}`)}
                          title="Edit"
                          sx={{ color: 'text.secondary' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
                      <MemberProfileAvatar member={member} size={120} sx={{ mb: 2 }} />

                      {/* Name */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          mb: 1,
                          textTransform: 'uppercase',
                          fontSize: '1rem',
                        }}
                      >
                        {member.first_name} {member.last_name}
                      </Typography>

                      {/* Email */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center', mb: 0.5, fontSize: '0.875rem' }}
                      >
                        {member.email}
                      </Typography>

                      {/* Phone */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center', mb: 2, fontSize: '0.875rem' }}
                      >
                        {member.phone}
                      </Typography>

                      {/* Status and Actions */}
                      <Box sx={{ width: '100%', mt: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={member.status}
                            color={getStatusColor(member.status)}
                            size="small"
                          />
                          {member.status === 'Pending' && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApprove(member.id)}
                                  disabled={updatingStatus === member.id}
                                >
                                  {updatingStatus === member.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Decline">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDecline(member.id)}
                                  disabled={updatingStatus === member.id}
                                >
                                  {updatingStatus === member.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CancelIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {member.status !== 'Pending' && (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={member.status}
                                onChange={(e) => handleStatusChange(member.id, e.target.value)}
                                disabled={updatingStatus === member.id}
                                sx={{
                                  fontSize: '0.75rem',
                                  height: '28px',
                                  '& .MuiSelect-select': { py: 0.5 },
                                }}
                              >
                                {statusChangeOptions.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Box>

                        {/* Constituency */}
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: 'center',
                            mt: 1,
                            pt: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            fontSize: '0.875rem',
                          }}
                        >
                          Constituency: {member.constituency || '-'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, total)} of {total} members
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rows per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Rows per page"
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                >
                  <MenuItem value={12}>12</MenuItem>
                  <MenuItem value={24}>24</MenuItem>
                  <MenuItem value={48}>48</MenuItem>
                  <MenuItem value={96}>96</MenuItem>
                </Select>
              </FormControl>
              <Pagination
                count={Math.ceil(total / rowsPerPage)}
                page={page + 1}
                onChange={(event, value) => handleChangePage(null, value - 1)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        </>
      )}

      {/* Bulk Action Confirmation Dialog */}
      <Dialog
        open={bulkActionDialog.open}
        onClose={() => setBulkActionDialog({ open: false, action: null })}
      >
        <DialogTitle>
          {bulkActionDialog.action === 'Active' ? 'Approve Members' : 'Decline Members'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {bulkActionDialog.action === 'Active'
              ? `Are you sure you want to approve ${selectedMembers.length} member(s)?`
              : `Are you sure you want to decline ${selectedMembers.length} member(s)? They will be marked as Suspended.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialog({ open: false, action: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleBulkAction(bulkActionDialog.action)}
            variant="contained"
            color={bulkActionDialog.action === 'Active' ? 'success' : 'error'}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!idCardsPdfReady}
        onClose={closeIdCardsPdfReadyDialog}
        maxWidth="xs"
        fullWidth
        aria-labelledby="id-cards-pdf-ready-title"
      >
        <DialogTitle id="id-cards-pdf-ready-title">ID card PDFs ready</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {idCardsPdfReady
              ? `Prepared a ZIP with ${idCardsPdfReady.count} PDF file(s)${
                  idCardsPdfReady.totalRequested
                    ? ` out of ${idCardsPdfReady.totalRequested} requested`
                    : ''
                }. Use the download button below — your browser may ask where to save the file.`
              : ''}
          </DialogContentText>
          {idCardsPdfReady?.failedExports?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {idCardsPdfReady.failedExports.length} member(s) were not included. A full list is in{' '}
              <strong>EXPORT-FAILURES.txt</strong> inside the ZIP.
            </Alert>
          )}
          {idCardsPdfReady?.failedExports?.length > 0 && (
            <Box
              component="ul"
              sx={{
                mt: 1.5,
                mb: 0,
                pl: 2.5,
                maxHeight: 220,
                overflow: 'auto',
                fontSize: '0.875rem',
              }}
            >
              {idCardsPdfReady.failedExports.map((f) => (
                <Box component="li" key={`${f.memberCode}-${f.label}`} sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {f.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {f.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeIdCardsPdfReadyDialog}>Cancel</Button>
          {idCardsPdfReady ? (
            <Button
              component="a"
              href={idCardsPdfReady.url}
              download={idCardsPdfReady.filename}
              variant="contained"
              autoFocus
              onClick={handleIdCardsPdfDownloaded}
            >
              Download ZIP
            </Button>
          ) : (
            <Button variant="contained" disabled>
              Download ZIP
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteDialogMember}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="delete-member-dialog-title"
      >
        <DialogTitle id="delete-member-dialog-title" color="error">
          Delete this member permanently?
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This cannot be undone. The member will be removed from the platform and the database, including profile,
            authentication, ID card, events, elections, invoices, and other linked records.
          </Alert>
          {deleteDialogMember && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                To confirm, type the membership ID{' '}
                <strong>{deleteDialogMember.member_id}</strong> (shown on their card and profile).
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label={`Type ${deleteDialogMember.member_id} to confirm`}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteDialogMember.member_id}
                variant="outlined"
                disabled={deleteSubmitting}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={submitPermanentDelete}
            variant="contained"
            color="error"
            disabled={
              deleteSubmitting ||
              !deleteDialogMember ||
              deleteConfirmText.trim() !== String(deleteDialogMember.member_id || '').trim()
            }
          >
            {deleteSubmitting ? 'Deleting…' : 'I understand, delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </ListPageLayout>
  );
}
